import React, { useState } from 'react';
import { StudioTextInput } from 'src/components/star-grid/components/StudioTextInput';
import Button from '@amzn/awsui-components-core/button';
import Alert from '@amzn/awsui-components-core/alert';
import Modal from '@amzn/awsui-components-core/modal';
import Input from '@amzn/awsui-components-core/input';
import Checkbox from '@amzn/awsui-components-core/checkbox';
import Multiselect from '@amzn/awsui-components-core/multiselect';
import FormField from '@amzn/awsui-components-core/form-field';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import type { StudioLineEntry, StudioLineSet } from 'src/components/star-grid/reportStudio.types';
import type { ReportStudioState } from 'src/components/star-grid/hooks/useReportStudio';
import { useStudioLineSets } from 'src/components/star-grid/hooks/useStudioLineSets';
import { useLineSetAliasOptions } from 'src/components/star-grid/hooks/useLineSetAliasOptions';
import { stripStrSuffix } from 'src/components/star-grid/utils/importedRowLineSet';
import { StudioSelect } from 'src/components/star-grid/components/StudioControls';
import { StudioFieldEditor } from 'src/components/star-grid/components/StudioFieldEditor';
import { StudioLinePicker } from 'src/components/star-grid/components/StudioLinePicker';
import { StudioReorderSlot } from 'src/components/star-grid/components/StudioReorderSlot';
import { STUDIO_LINE_REORDER_TYPE } from 'src/components/star-grid/reportStudioConstants';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';
import { StudioNestedRows } from 'src/components/star-grid/components/StudioNestedRows';

/**
 * The bulk-action controls for the checked lines.
 *
 * A component rather than inline JSX because it renders TWICE — once in the set header and once in a bar
 * pinned to the bottom of the viewport. With a long statement the header scrolls away, so acting on a
 * selection meant scrolling back up to reach the buttons.
 */
const LineSetToolbar: React.FC<{
  readonly set: StudioLineSet;
  readonly state: ReportStudioState;
  readonly editor: ReturnType<typeof useStudioLineSets>;
  readonly selected: ReadonlySet<string>;
  readonly onClear: () => void;
}> = ({ set, state, editor, selected, onClear }) => {
  const ids = set.entries.filter((entry) => selected.has(entry.id)).map((entry) => entry.id);
  const none = ids.length === 0;
  const firstIndex = set.entries.findIndex((entry) => selected.has(entry.id));
  const lastIndex = set.entries.reduce((found, entry, at) => (selected.has(entry.id) ? at : found), -1);
  return (
    <>
      <>
        <span className={styles.selectionCount}>
          {ids.length > 0 ? state.text('studioLineSelectedCount').replace('{count}', String(ids.length)) : ''}
        </span>
        <button
          type="button"
          className={`${styles.boldToggle} ${
            ids.length > 0 && ids.every((id) => set.entries.find((e) => e.id === id)?.emphasis === 'bold') ? styles.boldToggleOn : ''
          }`}
          disabled={none}
          aria-pressed={ids.length > 0 && ids.every((id) => set.entries.find((e) => e.id === id)?.emphasis === 'bold')}
          aria-label={state.text('studioLineBold')}
          title={state.text('studioLineBold')}
          onClick={() => editor.toggleBoldMany(set, ids)}
          data-testid="studio-lines-bold"
        >
          B
        </button>
        <span title={state.text('studioLineOutdent')} className={styles.toolbarTip}>
          <Button
            variant="icon"
            iconName="caret-left-filled"
            ariaLabel={state.text('studioLineOutdent')}
            disabled={none}
            onClick={() => editor.nudgeIndentMany(set, ids, -1)}
            data-testid="studio-lines-outdent"
          />
        </span>
        <span title={state.text('studioLineIndent')} className={styles.toolbarTip}>
          <Button
            variant="icon"
            iconName="caret-right-filled"
            ariaLabel={state.text('studioLineIndent')}
            disabled={none}
            onClick={() => editor.nudgeIndentMany(set, ids, 1)}
            data-testid="studio-lines-indent"
          />
        </span>
        <span title={state.text('studioLineInsertAbove')} className={styles.toolbarTip}>
          <Button
            variant="icon"
            iconName="add-plus"
            ariaLabel={state.text('studioLineInsertAbove')}
            disabled={none}
            onClick={() => editor.insert(set, Math.max(0, firstIndex), 'member', 'above')}
            data-testid="studio-lines-insert-above"
          />
        </span>
        <span title={state.text('studioLineInsertBelow')} className={styles.toolbarTip}>
          <Button
            variant="icon"
            iconName="insert-row"
            ariaLabel={state.text('studioLineInsertBelow')}
            disabled={none}
            onClick={() => editor.insert(set, Math.max(0, lastIndex), 'member', 'below')}
            data-testid="studio-lines-insert-below"
          />
        </span>
        <span title={state.text('studioMoveUp')} className={styles.toolbarTip}>
          <Button
            variant="icon"
            iconName="angle-up"
            ariaLabel={state.text('studioMoveUp')}
            disabled={none || firstIndex <= 0}
            onClick={() => editor.move(set, firstIndex, -1)}
            data-testid="studio-lines-up"
          />
        </span>
        <span title={state.text('studioMoveDown')} className={styles.toolbarTip}>
          <Button
            variant="icon"
            iconName="angle-down"
            ariaLabel={state.text('studioMoveDown')}
            disabled={none || lastIndex < 0 || lastIndex >= set.entries.length - 1}
            onClick={() => editor.move(set, lastIndex, 1)}
            data-testid="studio-lines-down"
          />
        </span>
        {/* An explicit DELETE, not a ✕. The cross read as "cancel" — it sat among icons and carried no
            verb — so a destructive action looked like a dismissal. */}
        <Button
          variant="normal"
          iconName="remove"
          disabled={none}
          onClick={() => {
            editor.removeMany(set, ids);
            onClear();
          }}
          data-testid="studio-lines-remove"
        >
          {state.text('studioLineDelete')}
        </Button>
        {/* CANCEL is its own control now and means "stop editing": it drops the selection, so no bulk
            action is left armed. */}
        <span title={state.text('studioLineCancelEdit')} className={styles.toolbarTip}>
          <Button
            variant="icon"
            iconName="close"
            ariaLabel={state.text('studioLineCancelEdit')}
            disabled={ids.length === 0}
            onClick={onClear}
            data-testid="studio-lines-cancel"
          />
        </span>
        {/* UNDO takes back the last edit, so a mis-click on Delete or Bold is recoverable without
            re-importing the template. */}
        <span title={state.text('studioLineUndo')} className={styles.toolbarTip}>
          <Button
            variant="icon"
            iconName="undo"
            ariaLabel={state.text('studioLineUndo')}
            disabled={!editor.canUndo}
            onClick={() => editor.undo()}
            data-testid="studio-lines-undo"
          />
        </span>
      </>
    </>
  );
};

export const StudioLineSets: React.FC<{ state: ReportStudioState }> = ({ state }) => {
  const editor = useStudioLineSets(state);
  // Which row's display name is open for typing. One at a time: the column is a value with a pencil, not
  // a grid of always-live inputs.
  const [editingName, setEditingName] = useState<string | undefined>(undefined);
  // Checked lines. One toolbar acts on these, instead of seven buttons repeating on every row.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const toggleSelected = (id: string, checked: boolean): void =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  const line = editor.line;
  // The dimension the EDITED LINE draws its members from — the hierarchy the member picker opens. A set
  // holds the rows of every dimension placed on Rows, so the line's own binding wins over the set's.
  const pickingBinding =
    editor.pickingSet && state.dataset.bindings.find((item) => item.id === (editor.pickingLine?.binding_id ?? editor.pickingSet!.binding_id));
  // The FIRST set's dimension drives the display-name suggestions: the editor shows one set's lines at a
  // time in practice, and every set on screen belongs to the same row dimension.
  const setBinding = state.dataset.bindings.find((item) => item.id === state.deck.line_sets[0]?.binding_id);
  const deckOptions = Object.keys(state.draft.definition.decks ?? {});
  // What the picker shows when nothing is chosen: the active deck, which is what an edit lands on.
  const activeDeckIds = state.draft.definition.active_deck ? [state.draft.definition.active_deck] : [];
  const alias = useLineSetAliasOptions(setBinding?.dimension, setBinding?.hierarchy);
  return (
    <div className={styles.lineSets}>
      <StudioNestedRows axis={state.nestedRows} text={state.text} listMembers />
      {!state.deck.line_sets.length && <Alert>{state.text('studioNoLineSets')}</Alert>}
      {[...state.deck.line_sets]
        .sort((a, b) => Number(b.id === state.focusedLineSet) - Number(a.id === state.focusedLineSet))
        .map((set) => {
          const expansion = editor.expansion(set);
          return (
            <section className={styles.shelf} key={set.id}>
              <header className={styles.panelHeader}>
                <h2>{set.label}</h2>
                <span className={styles.previewTag}>{set.version}</span>
                <span className={styles.spacer} />
                {set.editable && (
                  <>
                    {/* ONE toolbar for the checked rows, instead of the same seven buttons on every line.
                    Every control is disabled until something is checked, so the row it would act on is
                    never ambiguous, and each carries a `title` tooltip because the icons alone (a bare
                    `B`, carets) do not say what they do. */}
                    {/* Which decks an edit lands on. Defaults to the ACTIVE deck only, so nothing changes
                    scope by accident; widening it applies every subsequent edit — bold, indent, insert,
                    delete, rename — to the chosen decks in one write. */}
                    {deckOptions.length > 1 && (
                      // Fixed basis and a visible label: the header WRAPS, and an unconstrained Multiselect
                      // took a greedy width that pushed "Add line" onto the next row. `hideTokens` keeps it
                      // one line high — the chips stacked as decks were ticked, which is what made it hard to
                      // read — so it reads as a summary instead of growing.
                      <span className={styles.deckScope}>
                        <span className={styles.fieldLabel}>{state.text('studioLineApplyToDecks')}</span>
                        <Multiselect
                          selectedOptions={(state.lineSetDecks.length > 0 ? state.lineSetDecks : activeDeckIds).map((deck) => ({
                            value: deck,
                            label: deck
                          }))}
                          options={deckOptions.map((deck) => ({ value: deck, label: deck }))}
                          hideTokens
                          // Portals the dropdown to the body. The set's panel clips overflow, so the popup
                          // opened INSIDE it and was cut off — it looked like the control expanded to nothing.
                          // Every other select in the studio does this for the same reason.
                          expandToViewport
                          placeholder={state.text('studioLineApplyToDecks')}
                          ariaLabel={state.text('studioLineApplyToDecks')}
                          onChange={({ detail }) =>
                            state.setLineSetDecks(detail.selectedOptions.flatMap((option) => (option.value ? [option.value] : [])))
                          }
                          data-testid="studio-lines-decks"
                        />
                      </span>
                    )}
                    <Button onClick={() => editor.add(set, 'calculation')}>{state.text('studioAddLine')}</Button>
                    <LineSetToolbar set={set} state={state} editor={editor} selected={selected} onClear={() => setSelected(new Set())} />
                  </>
                )}
              </header>
              <div className={styles.shelfBody}>
                {!set.editable && <p className={styles.help}>{state.text('studioImportedLineSet')}</p>}
                {/* Column headers: the two names a line carries are different things, and an unlabelled pair
                of columns left it ambiguous which one the report renders. */}
                <div className={styles.lineColumnHeader}>
                  {/* Select-all for the toolbar above, in the column the per-row checkboxes sit in. */}
                  <span className={styles.lineColumnIndex}>
                    <Checkbox
                      checked={expansion.visible.length > 0 && expansion.visible.every((entry) => selected.has(entry.id))}
                      indeterminate={
                        expansion.visible.some((entry) => selected.has(entry.id)) && !expansion.visible.every((entry) => selected.has(entry.id))
                      }
                      disabled={!set.editable}
                      ariaLabel={state.text('studioLineSelectAll')}
                      onChange={({ detail }) => setSelected(detail.checked ? new Set(expansion.visible.map((entry) => entry.id)) : new Set())}
                      data-testid="studio-lines-select-all"
                    />
                  </span>
                  <span className={styles.lineColumnElement}>{state.text('studioLineElementName')}</span>
                  <span className={styles.lineColumnDisplay}>{state.text('studioLineDisplayName')}</span>
                  <span className={styles.lineColumnIcon}>{state.text('studioLineExpandable')}</span>
                  <span className={styles.spacer} />
                </div>
                {set.entries.map((entry, index) =>
                  expansion.hidden.has(entry.id) ? null : (
                    <StudioReorderSlot
                      key={entry.id}
                      index={index}
                      dragType={STUDIO_LINE_REORDER_TYPE}
                      label={entry.label || entry.member_id || entry.kind}
                      disabled={!set.editable}
                      onReorder={(from, to) => editor.move(set, from, to - from)}
                      // Double-click inserts BELOW, the same as the row's own insert button — a shortcut for
                      // building a statement top-down without reaching for the toolbar each time.
                      onDoubleClick={set.editable ? () => editor.insert(set, index, 'member', 'below') : undefined}
                      className={`${styles.lineRow} ${entry.kind === 'section' ? styles.sectionLine : ''} ${
                        entry.kind === 'spacer' ? styles.spacerLine : ''
                      }`}
                    >
                      {/* A checkbox where the row number used to be. The number was decoration — nothing
                    addressed a line by it — and the space is worth more as a selector, which is what lets
                    one toolbar replace the seven buttons that used to repeat on every row. */}
                      <span className={styles.lineColumnIndex}>
                        <Checkbox
                          checked={selected.has(entry.id)}
                          disabled={!set.editable}
                          ariaLabel={`${state.text('studioLineSelect')}: ${entry.label || entry.member_id || String(index + 1)}`}
                          onChange={({ detail }) => toggleSelected(entry.id, detail.checked)}
                          data-testid={`studio-line-select-${entry.id}`}
                        />
                      </span>
                      {/* The indent the row will RENDER with, shown here too. It was stored and applied in the
                    grid but invisible in this editor, so a statement's shape could only be checked by
                    previewing it. Class per depth rather than an inline style, and `MAX_LINE_INDENT`
                    caps depth at 4 so the set is closed. */}
                      <span className={styles.lineColumnElement}>
                        {expansion.nodes.has(entry.id) && (
                          <Button
                            variant="icon"
                            iconName={expansion.isExpanded(entry) ? 'caret-down-filled' : 'caret-right-filled'}
                            ariaLabel={`${state.text(expansion.isExpanded(entry) ? 'studioLineCollapse' : 'studioLineExpand')}: ${
                              entry.label || entry.member_id || String(index + 1)
                            }`}
                            disabled={!set.editable}
                            onClick={() => editor.toggleLineExpanded(set, entry)}
                            data-testid={`studio-line-expand-${entry.id}`}
                          />
                        )}
                        <span
                          className={`${entry.emphasis === 'bold' ? styles.lineBold : styles.lineNormal} ${
                            styles[`indent${Math.min(4, Math.max(0, entry.indent ?? 0))}`]
                          }`}
                        >
                          {entry.member_id || entry.label || state.text(`studioLine_${entry.kind}`)}
                        </span>
                        {/* Pencil beside the NAME, as the report-studio shelves place it, instead of a trailing
                    "Edit" link at the far right of a wide row. */}
                        {set.editable && (
                          <Button
                            variant="icon"
                            iconName="edit"
                            ariaLabel={`${state.text('studioEdit')}: ${entry.label || entry.member_id || String(index + 1)}`}
                            ariaHaspopup="dialog"
                            onClick={() =>
                              entry.kind === 'member'
                                ? editor.setPicking({ setId: set.id, lineId: entry.id })
                                : editor.setEditing({ setId: set.id, lineId: entry.id })
                            }
                            data-testid={`studio-line-edit-${entry.id}`}
                          />
                        )}
                        <small>
                          {entry.operator
                            ? `${entry.operator} (${entry.references
                                ?.map(
                                  (id, refIndex) => `${entry.weights?.[refIndex] ?? 1} × ${set.entries.find((item) => item.id === id)?.label || id}`
                                )
                                .join(', ')})`
                            : // The element has its own column now, so repeating it here said nothing.
                              ''}
                        </small>
                      </span>
                      {/* REPORT DISPLAY NAME — what the row is labelled in the rendered report. Its own pencil,
                    so the two names are edited by two clearly separate controls: the element pencil picks
                    WHICH member the row is, this one names it. Held on `label`, the field the grid renders,
                    so a row never has two competing names. */}
                      <span className={styles.lineColumnDisplay}>
                        {editingName === entry.id ? (
                          <>
                            <StudioTextInput
                              value={entry.label}
                              autoFocus
                              placeholder={entry.member_id ?? ''}
                              ariaLabel={`${state.text('studioLineDisplayName')}: ${entry.member_id ?? index + 1}`}
                              onCommit={(label) => editor.update({ ...entry, label })}
                              onBlur={() => setEditingName(undefined)}
                              onEnter={() => setEditingName(undefined)}
                              onEscape={() => setEditingName(undefined)}
                              data-testid={`studio-line-display-name-${entry.id}`}
                            />
                            {/* Alias values for THIS element, when the dimension exports any. Only twelve do, so
                          the picker is absent for the rest and the field is purely hand-entered. */}
                            {(alias.optionsByMember[(entry.member_id ?? '').toLowerCase()]?.length ?? 0) > 0 && (
                              <StudioSelect
                                label={state.text('studioLineDisplayNameAlias')}
                                value={entry.label}
                                selectedLabel={entry.label}
                                options={(alias.optionsByMember[(entry.member_id ?? '').toLowerCase()] ?? []).map((value) => ({
                                  id: value,
                                  label: value
                                }))}
                                onChange={(value) => editor.update({ ...entry, label: value })}
                              />
                            )}
                          </>
                        ) : (
                          <>
                            {/* Rendered the way the REPORT renders this row -- its indent, bold, section title
                            and spacer gap -- so the set reads as the statement it produces. */}
                            <span
                              className={`${styles.lineDisplayValue} ${styles[`reportIndent${Math.min(4, Math.max(0, entry.indent ?? 0))}`]} ${
                                entry.emphasis === 'bold' ? styles.lineBold : ''
                              } ${entry.kind === 'section' ? styles.reportSection : ''}`}
                            >
                              {entry.kind === 'spacer' ? '' : stripStrSuffix(entry.label) || '—'}
                            </span>
                            {set.editable && entry.kind !== 'spacer' && (
                              <Button
                                variant="icon"
                                iconName="edit"
                                ariaLabel={`${state.text('studioLineEditDisplayName')}: ${entry.member_id ?? index + 1}`}
                                onClick={() => setEditingName(entry.id)}
                                data-testid={`studio-line-display-name-edit-${entry.id}`}
                              />
                            )}
                          </>
                        )}
                      </span>
                      <span className={styles.lineColumnIcon}>
                        {expansion.nodes.has(entry.id) && (
                          <Checkbox
                            checked={expansion.isExpandable(entry)}
                            disabled={!set.editable}
                            onChange={() => editor.toggleLineExpandable(set, entry)}
                            ariaLabel={`${state.text('studioLineExpandable')}: ${entry.label || entry.member_id || String(index + 1)}`}
                            data-testid={`studio-line-expandable-${entry.id}`}
                          />
                        )}
                      </span>
                      <span className={styles.spacer} />
                    </StudioReorderSlot>
                  )
                )}
              </div>
              {/* The SAME controls, pinned to the bottom while a selection exists. A statement runs to
              hundreds of lines, so the header toolbar scrolls out of view and finishing an action meant
              scrolling back up. Rendered only when something is checked, so it never covers content
              needlessly. */}
              {set.editable && set.entries.some((entry) => selected.has(entry.id)) && (
                <div className={styles.stickyLineToolbar} data-testid="studio-lines-sticky-toolbar">
                  <LineSetToolbar set={set} state={state} editor={editor} selected={selected} onClear={() => setSelected(new Set())} />
                </div>
              )}
            </section>
          );
        })}
      <Modal
        visible={Boolean(line)}
        header={state.text('studioEditLine')}
        closeAriaLabel={state.text('studioClose')}
        onDismiss={() => editor.setEditing(undefined)}
        footer={<Button onClick={() => editor.setEditing(undefined)}>{state.text('studioDone')}</Button>}
      >
        {line && editor.set && (
          <SpaceBetween size="m">
            <FormField label={state.text('studioLabel')}>
              <StudioTextInput value={line.label} onCommit={(label) => editor.update({ ...line, label })} />
            </FormField>
            <StudioSelect
              label={state.text('studioLineKind')}
              value={line.kind}
              options={(['member', 'calculation', 'ratio', 'section', 'spacer'] as const).map((id) => ({
                id,
                label: state.text(`studioLine_${id}`)
              }))}
              onChange={(kind) =>
                editor.update({
                  id: line.id,
                  label: line.label,
                  kind: kind as StudioLineEntry['kind'],
                  ...(kind === 'calculation' || kind === 'ratio'
                    ? { operator: kind === 'ratio' ? ('ratio' as const) : ('sum' as const), references: [], weights: [] }
                    : {})
                })
              }
            />
            {line.kind === 'member' && (
              <StudioFieldEditor
                singleMember
                shelf="rows"
                field={{
                  id: line.id,
                  binding_id: line.binding_id ?? editor.set.binding_id,
                  selection: { kind: 'members', members: line.member_id ? [line.member_id] : [] }
                }}
                dataset={state.dataset}
                lineSets={[]}
                text={state.text}
                onChange={(field) => editor.update({ ...line, member_id: field.selection.members[0] })}
                onMove={() => undefined}
                onRemove={() => editor.update({ ...line, member_id: undefined })}
              />
            )}
            {(line.kind === 'ratio' || line.kind === 'calculation') && (
              <>
                <StudioSelect
                  label={state.text('studioOperator')}
                  value={line.operator ?? ''}
                  options={(['sum', 'difference', 'ratio'] as const).map((id) => ({ id, label: state.text(`studioOperator_${id}`) }))}
                  onChange={(operator) => editor.update({ ...line, operator: operator as StudioLineEntry['operator'] })}
                />
                {(line.references ?? []).map((id, index) => (
                  <div className={styles.pair} key={index}>
                    <StudioSelect
                      label={state.text('studioReference')}
                      value={id}
                      options={editor
                        .set!.entries.filter((item) => item.id !== line.id && ['member', 'calculation', 'ratio'].includes(item.kind))
                        .map((item) => ({ id: item.id, label: item.label }))}
                      onChange={(reference) =>
                        editor.update({ ...line, references: line.references!.map((item, i) => (i === index ? reference : item)) })
                      }
                    />
                    <FormField label={state.text('studioWeight')}>
                      <Input
                        type="number"
                        value={String(line.weights?.[index] ?? 1)}
                        onChange={({ detail }) =>
                          editor.update({
                            ...line,
                            weights: line.references!.map((_, i) => (i === index ? Number(detail.value) : line.weights?.[i] ?? 1))
                          })
                        }
                      />
                    </FormField>
                    <Button
                      variant="icon"
                      iconName="close"
                      ariaLabel={state.text('studioRemoveReference')}
                      onClick={() =>
                        editor.update({
                          ...line,
                          references: line.references!.filter((_, i) => i !== index),
                          weights: line.references!.filter((_, i) => i !== index).map((_, i) => line.weights?.[i < index ? i : i + 1] ?? 1)
                        })
                      }
                    />
                  </div>
                ))}
                <Button
                  onClick={() =>
                    editor.update({
                      ...line,
                      references: [...(line.references ?? []), ''],
                      weights: [...(line.references?.map((_, index) => line.weights?.[index] ?? 1) ?? []), 1]
                    })
                  }
                >
                  {state.text('studioAddReference')}
                </Button>
              </>
            )}
            <StudioSelect
              label={state.text('studioFormat')}
              value={line.format ?? 'number'}
              options={(['number', 'currency', 'percentage', 'basisPoints'] as const).map((id) => ({ id, label: state.text(`studioFormat_${id}`) }))}
              onChange={(format) => editor.update({ ...line, format: format as StudioLineEntry['format'] })}
            />
          </SpaceBetween>
        )}
      </Modal>
      {editor.picking && editor.pickingLine && pickingBinding && (
        <StudioLinePicker
          dataset={state.dataset}
          bindingId={pickingBinding.id}
          members={editor.pickingLine.member_id ? [editor.pickingLine.member_id] : []}
          text={state.text}
          onConfirm={(members) => editor.applyMembers(members)}
          onDismiss={() => editor.setPicking(undefined)}
          testId={`studio-line-members-${editor.pickingLine.id}`}
        />
      )}
      {state.issues
        .filter((issue) => issue.path.includes('line_sets'))
        .map((issue) => (
          <Alert key={issue.path + issue.key} type="warning">
            {state.text(issue.key)}
          </Alert>
        ))}
    </div>
  );
};
